import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { getSignupSource } from '../lib/signupSource'

const AuthContext = createContext({})

const generateReferralCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let s = ''
  for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)]
  return s
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user)
      setLoading(false)
    }).catch(() => setLoading(false)) // never leave protected pages spinning
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user)
      else setProfile(null)
    })
    return () => subscription.unsubscribe()
  }, [])

  const fetchProfile = async (authUser) => {
    const userId = typeof authUser === 'string' ? authUser : authUser?.id
    if (!userId) return
    let { data } = await supabase.from('profiles').select('*').eq('id', userId).single()
    // Backfill suburb from signup metadata if the profile was created without one.
    // handle_new_user trigger only sets id + full_name; we stash suburb in auth user_metadata
    // at signup, then copy it across on the first authenticated profile load.
    const metaSuburb = typeof authUser === 'object' ? authUser?.user_metadata?.suburb?.trim() : null
    if (data && !data.suburb && metaSuburb) {
      const { data: updated } = await supabase
        .from('profiles').update({ suburb: metaSuburb }).eq('id', userId).select().single()
      if (updated) data = updated
    }

    // Generate the user's own referral_code if missing — same first-load backfill pattern as suburb.
    if (data && !data.referral_code) {
      const code = generateReferralCode()
      const { data: updated } = await supabase
        .from('profiles').update({ referral_code: code }).eq('id', userId).select().single()
      if (updated) data = updated
    }

    // Record the referral if they came via a ref code at signup.
    // All errors are swallowed: a missing ref, a code that doesn't match anyone, a self-referral,
    // or a unique-constraint violation (already recorded) all should be silent no-ops. The referrals
    // table enforces no-self-referral and uniqueness at the DB level.
    const refCode = typeof authUser === 'object' ? authUser?.user_metadata?.referred_by_code : null
    if (refCode) {
      try {
        const { data: referrer } = await supabase
          .from('public_profiles').select('id').eq('referral_code', refCode).maybeSingle()
        if (referrer && referrer.id !== userId) {
          await supabase.from('referrals').insert({
            referrer_id: referrer.id,
            referred_id: userId,
            status: 'pending'
          })
        }
      } catch (e) {
        // Silent — see comment above.
      }
    }

    setProfile(data)
  }

  const refreshProfile = () => user && fetchProfile(user)

  // signup_source / signup_form are copied onto the profile by the profiles_copy_signup_details trigger
  const signUp = async (email, password, fullName, suburb, refCode, redirectTo, signupForm) => {
    const opts = { data: { full_name: fullName, suburb: suburb?.trim() || null, referred_by_code: refCode || null, signup_source: getSignupSource(), signup_form: signupForm || null } }
    if (redirectTo) opts.emailRedirectTo = redirectTo
    const { data, error } = await supabase.auth.signUp({ email, password, options: opts })
    return { data, error }
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    return { data, error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
  }

  const updateProfile = async (updates) => {
    const { data, error } = await supabase
      .from('profiles').update(updates).eq('id', user.id).select().single()
    if (!error) setProfile(data)
    return { data, error }
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signUp, signIn, signOut, updateProfile, fetchProfile, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
